"use client"

import { Check } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { useTranslation } from "react-i18next"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { captureEvent } from "@/utils/analytics";

export interface PricingPlanProps {
  name: string
  price: {
    monthly: string
    yearly: string
  }
  description: string
  features: string[]
  isPopular?: boolean
  isBestValue?: boolean
  stripePriceIds?: {
    yearly: string
  }
  savingsPercentage?: number
}

export const PricingPlan = ({
  name,
  price,
  description,
  features = [],
  isPopular,
  isBestValue,
  stripePriceIds,
  savingsPercentage = 25,
}: PricingPlanProps) => {
  const { t } = useTranslation("pricing")
  const { toast } = useToast()

  const handleStartTrial = async () => {
    // Track trial start in PostHog
    captureEvent("start_trial", { plan: name, billingCycle: "yearly", source: "PricingPlan" });

    if (name === "Premium" || name === "Cao Cấp") {
      // Redirect to Calendly for Premium plan
      window.location.href = "https://cal.com"
      return
    }

    if (!stripePriceIds) {
      toast({
        title: "Configuration Error",
        description: "Payment setup is not configured for this plan.",
        variant: "destructive",
      })
      return
    }

    const currentPriceId = stripePriceIds.yearly

    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId: currentPriceId },
      })

      const url = data?.url

      if (error) throw error
      if (!url) throw new Error("No checkout URL returned")

      // Redirect to Stripe Checkout
      window.location.href = url
    } catch (error) {
      console.error("Payment error:", error)
      toast({
        title: "Error",
        description: "Could not initiate payment. Please try again.",
        variant: "destructive",
      })
    }
  }

  // Ensure features is an array before mapping
  const featuresArray = Array.isArray(features) ? features : []

  return (
    <Card
      className={`p-6 md:p-8 relative hover:shadow-xl transition-shadow duration-300 h-full flex flex-col ${
        isPopular ? "border-blue-500 shadow-lg" : isBestValue ? "border-purple-500 shadow-lg" : ""
      }`}
    >
      {isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium">
          {t("mostPopular")}
        </div>
      )}
      {isBestValue && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium">
          {t("bestValue")}
        </div>
      )}

      <div className="mb-8">
        <h3 className="text-2xl font-bold mb-2">{name}</h3>
        <div className="text-4xl font-bold mb-2">
          {price.yearly}
          <span className="text-lg text-gray-500 font-normal ml-1">{t("perYear")}</span>
        </div>

        {/* Show monthly equivalent */}
        <div className="text-sm text-gray-500 mb-4">
          {t("equivalentTo", { defaultValue: "Equivalent to" })} {price.monthly} {t("perMonth")}
        </div>

        {/* Show savings badge */}
        <div className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full inline-block mb-4">
          {t("save", { defaultValue: "Save" })} {savingsPercentage}%
        </div>

        <p className="text-gray-600 mb-6">{description}</p>
      </div>

      <div className="flex-grow">
        <h4 className="font-semibold mb-4 text-lg">{t("features", { defaultValue: "Features" })}</h4>
        <ul className="space-y-4 mb-8">
          {featuresArray.map((feature, index) => (
            <li key={index} className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
              <span className="text-gray-600">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <Button
        onClick={handleStartTrial}
        className={`w-full mt-auto ${isPopular ? "bg-blue-600 hover:bg-blue-700" : isBestValue ? "bg-purple-600 hover:bg-purple-700" : ""}`}
        size="lg"
      >
        {t("startTrial")}
      </Button>
    </Card>
  )
}
